import { Component, ElementRef, inject, Input, OnDestroy, TemplateRef } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  ControlValueAccessor,
  FormArray,
  FormControl,
  NgControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { SelectionModel } from '@angular/cdk/collections';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { HasIdName } from '../model';

export interface IdNamePairProperty {
  id: string;
  name: string;
  propertyKey: string;
  type: 'NUMBER' | 'BOOLEAN' | 'TEXT' | 'SELECTION';
  options: string | null;
}

export interface IdNamePairChildren extends HasIdName {
  children?: IdNamePairChildren[];
  properties?: IdNamePairProperty[];
  /** Parent-qualified display name ("Primary packaging › Paper") — set by flatteners for disambiguation. */
  qualifiedName?: string;
}

function flattenAll(nodes: IdNamePairChildren[]): IdNamePairChildren[] {
  const out: IdNamePairChildren[] = [];
  for (const n of nodes) {
    out.push(n);
    if (n.children?.length) out.push(...flattenAll(n.children));
  }
  return out;
}

/** The whole subtree strictly below `node` — flattenAll already recurses. */
function descendantsOf(node: IdNamePairChildren): IdNamePairChildren[] {
  return node.children?.length ? flattenAll(node.children) : [];
}

@Component({
  selector: 'tree-selection[values]',
  template: `
    <mat-tree [dataSource]="dataSource" [treeControl]="treeControl">
      <mat-nested-tree-node *matTreeNodeDef="let node" [disabled]="disabled">
        <div style="display:flex; flex-direction: row; align-items:center">
          @if (node.children) {
            <button type="button" [disabled]="disabled || isDisabled(node)" mat-icon-button matTreeNodeToggle>
              <mat-icon class="mat-icon-rtl-mirror">
                {{ treeControl.isExpanded(node) ? 'expand_more' : 'chevron_right' }}
              </mat-icon>
            </button>
          } @else {
            <!-- Same-width filler so leaf checkboxes align with sibling branch checkboxes. -->
            <button type="button" mat-icon-button disabled aria-hidden="true" tabindex="-1"></button>
          }
          <mat-checkbox
            (change)="toggleSelection(node)"
            [checked]="isChecked(node)"
            [disabled]="disabled || isDisabled(node)"
            [indeterminate]="isIndeterminate(node)"
            [title]="node.name"
          >
            {{ node.name }}
          </mat-checkbox>
        </div>
        @if (leafContent && !node.children && isChecked(node)) {
          <div style="margin-left:40px;">
            <ng-container *ngTemplateOutlet="leafContent; context: { $implicit: node }"></ng-container>
          </div>
        }
        @if (treeControl.isExpanded(node)) {
          <div style="margin-left:70px;">
            <ng-container matTreeNodeOutlet></ng-container>
          </div>
        }
      </mat-nested-tree-node>
    </mat-tree>
  `,
  styles: [' mat-checkbox { overflow: hidden; white-space: nowrap; } '],
  providers: [{ provide: MatFormFieldControl, useExisting: TreeSelectionInputComponent }],
  host: {
    '[id]': 'id',
    '(focusin)': 'onFocusIn()',
    '(focusout)': 'onFocusOut($event) //noinspection UnresolvedReference',
    role: 'group',
  },
  imports: [ReactiveFormsModule, MatCheckboxModule, MatTreeModule, MatIconModule, MatButtonModule, NgTemplateOutlet],
})
export class TreeSelectionInputComponent implements ControlValueAccessor, MatFormFieldControl<HasIdName[]>, OnDestroy {
  private _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  ngControl = inject(NgControl, { optional: true, self: true });

  @Input() leafContent: TemplateRef<{ $implicit: IdNamePairChildren }> | null = null;

  /**
   * Whether a BRANCH node is itself a selectable member of the value.
   *
   * `true` (default) — attribute-definition trees: a branch carries properties of
   * its own (e.g. "Primary packaging" → Packaging Type), so it is a valid member.
   *
   * `false` — document-type/criteria trees: the branch is a DocumentType, which is
   * NOT a valid criterion (LegalNormController resolves the value through
   * documentCriteriaRepository.findAllById, silently dropping a type id). Toggling
   * a branch then covers its DESCENDANTS only, and its checkbox state is DERIVED
   * from them — otherwise a fully-checked type would come back indeterminate after
   * a reload, since its id was never persisted.
   */
  @Input() selectableBranches = true;

  static nextId = 0;
  controls = new FormArray<FormControl<boolean | null>>([]);
  stateChanges = new Subject<void>();
  focused = false;
  touched = false;
  controlType = 'tree-selection';
  id = `tree-selection-${TreeSelectionInputComponent.nextId++}`;
  shouldLabelFloat = true;
  treeControl = new NestedTreeControl(({ children }: IdNamePairChildren) => children);
  selection = new SelectionModel<HasIdName>(true);
  dataSource = new MatTreeNestedDataSource<IdNamePairChildren>();

  constructor() {
    (this.ngControl as NgControl).valueAccessor = this;
  }

  private _values: IdNamePairChildren[] = [];

  @Input()
  set values(values: IdNamePairChildren[]) {
    this.dataSource.data = values;
    this._values = values;
    this.controls = new FormArray(this._value.map(() => new FormControl(true)));
    this.stateChanges.next();
  }

  get empty() {
    return !this.controls.value.length;
  }

  private _placeholder!: string;

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }

  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }

  private _required = false;

  @Input()
  get required(): boolean {
    return this._required || !!this.ngControl?.control?.hasValidator(Validators.required);
  }

  set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  private _disabled = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    void (this._disabled ? this.controls.disable() : this.controls.enable());
    this.stateChanges.next();
  }

  _value: HasIdName[] = [];

  @Input()
  get value(): HasIdName[] | null {
    return this.controls.valid ? this._value : null;
  }

  set value(value: HasIdName[] | null) {
    this._value = [...(value || [])];
    this.controls = new FormArray(this._value.map(() => new FormControl(true)));
    this.stateChanges.next();
  }

  get errorState(): boolean {
    return (this.controls.invalid || !!this.ngControl?.control?.invalid) && this.touched;
  }

  setDescribedByIds = () => void 0;
  onContainerClick = () => void 0;
  onChange: (value: HasIdName[] | null) => void = () => void 0;
  onTouched = () => void 0;
  registerOnChange = (fn: any): void => (this.onChange = fn);
  registerOnTouched = (fn: any): void => (this.onTouched = fn);

  ngOnDestroy() {
    this.stateChanges.complete();
  }

  onFocusIn() {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent) {
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  writeValue(selection: HasIdName[] | null): void {
    const ids = (selection || []).map(({ id }) => id);
    this.selection.clear();
    this.selection.setSelection(...flattenAll(this._values).filter(({ id }) => ids.includes(id)));
    this.value = selection;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** A node with at least one child — an empty-children node is a (disabled) leaf-like branch. */
  private isBranch = (node: IdNamePairChildren) => !!node.children?.length;

  /** Branch state when `selectableBranches` is false: derived from the subtree. */
  private allDescendantsSelected(node: IdNamePairChildren): boolean {
    const descendants = descendantsOf(node);
    return descendants.length > 0 && descendants.every((d) => this.selection.isSelected(d));
  }

  toggleSelection(node: IdNamePairChildren): void {
    // Selection is explicit per node: a checked branch is itself a member (branches can carry
    // properties, e.g. "Primary packaging" → Packaging Type) AND owns its whole subtree.
    // With `selectableBranches = false`, the branch itself is never a member — only its subtree.
    const coversBranch = this.selectableBranches || !this.isBranch(node);
    const subtree = coversBranch ? [node, ...descendantsOf(node)] : descendantsOf(node);
    if (this.isChecked(node)) {
      this.selection.deselect(...subtree);
    } else {
      this.selection.select(...subtree);
    }
    this.updateControl();
  }

  isDisabled = (node: IdNamePairChildren) => !!node.children && !node.children.length;

  isChecked = (node: IdNamePairChildren) =>
    !this.selectableBranches && this.isBranch(node)
      ? this.allDescendantsSelected(node)
      : this.selection.isSelected(node);

  isIndeterminate = (node: IdNamePairChildren) =>
    !this.isChecked(node) && descendantsOf(node).some((d) => this.selection.isSelected(d));

  private updateControl() {
    this.value = this.selection.selected;
    this.onChange(this.value);
  }
}
